<template>
  <section class="nylon-ShopContent" id="shop-content">
    <shop-feature v-if="localFeaturedProducts.length" type="billboards" color="slate" background="pattern" wrapper-class="billboard-wrapper" :items="localFeaturedProducts" auto-scroll>
      <!-- Campaign billboards and the full-bleed takeover go in the `prepend` slot; see /admin/patterns/shop-feature-billboards and shop-feature-takeover for the markup. -->
      <template v-slot:item="{ item }">
        <billboard type="product" :productType="item.type" :color="mapBillboardColor(item.theme)" :url="item.links.url">
          <template v-slot:background>
            <ProductImageV2
              :productId="item.id"
              :detail="true"
              :hash="item.imageCacheHash"
              :alt="`${item.title} by ${item.designerName}`"
              sizes="(min-width: 1920px) 360px, (min-width: 1600px) 320px, (min-width: 1280px) 300px, (min-width: 992px) 280px, (min-width: 768px) 240px, 120px"
              :imgixParams="{ q: 1 }"
              :showLoader="false"
            />
          </template>
          <template v-slot:image-frosted>
            <ProductImageV2
              :productId="item.id"
              :detail="true"
              :hash="item.imageCacheHash"
              :alt="`${item.title} by ${item.designerName}`"
              sizes="(min-width: 1920px) 360px, (min-width: 1600px) 320px, (min-width: 1280px) 300px, (min-width: 992px) 280px, (min-width: 768px) 240px, 120px"
              :imgixParams="{ q: 1 }"
              :showLoader="false"
            />
          </template>
          <template v-slot:image>
            <ProductImageV2
              :productId="item.id"
              :default="true"
              :hash="item.imageCacheHash"
              :alt="`${item.title} by ${item.designerName}`"
              sizes="(min-width: 1920px) 360px, (min-width: 1600px) 320px, (min-width: 1280px) 300px, (min-width: 992px) 280px, (min-width: 768px) 240px, 120px"
            />
          </template>
          <template v-if="isAdmin" v-slot:admin>
            <button class="billboard-featured-remove" @click.prevent="removeFeatured(item)">
              <NylonIcon icon-name="uiEye" viewbox="0 0 24 24" classes="icon" />
            </button>
          </template>
          <template v-slot:label>Featured Product</template>
          <template v-slot:title>{{ item.title }}</template>
          <template v-slot:text>
            <p>{{ item.designerName }}</p>
          </template>
        </billboard>
      </template>
    </shop-feature>

    <ais-instant-search
      ref="instantSearch"
      :search-client="searchClient"
      :index-name="searchIndexName"
      :on-state-change="onSearchStateChange"
      :middlewares="searchMiddlewares"
      :routing="routing"
      id="search-content"
    >
      <ais-configure :filters="baseFilters" />
      <section class="nylon-ShopWrapper">
        <!-- ///// Filter Column ///// -->
        <search-controls
          :hitsPerPage="hitsPerPage"
          :showBuyItNow="showBuyItNow"
          :showCollections="showCollections"
          :showApparelFacets="showApparelFacets"
          :showHatFacets="showHatFacets"
          :showPhoneCaseFacets="showPhoneCaseFacets"
          :showDrinkwareFacets="showDrinkwareFacets"
        />

        <search-results :showBuyItNow="showBuyItNow" :showCollections="showCollections" />
      </section>
    </ais-instant-search>
  </section>
</template>

<script>
// SearchControls and SearchResults are loaded synchronously to ensure their widgets register before the initial search.
import SearchControls from "@/shop/search-controls.vue";
import SearchResults from "@/shop/search-results.vue";
// The billboards head the page, so they load with it rather than shifting the page in later.
import ShopFeature from "@/shop/shop-feature.vue";
import Billboard from "@/shop/components/billboard.vue";
import ProductImageV2 from "@/product/components/product-image-v2.vue";
import SearchMixin from "@/mixins/search.js";
import { AisConfigure, AisInstantSearch } from "vue-instantsearch/vue3/es";
import { mapState } from "pinia";
import { useUserStore } from "@/store/user.js";
import { useAdminProductsStore } from "@/admin/store/products.js";

export default {
  components: {
    AisConfigure,
    AisInstantSearch,
    SearchControls,
    SearchResults,
    ProductImageV2,
    ShopFeature,
    Billboard,
  },

  props: {
    featuredProducts: {
      type: Array,
      required: false,
      default: () => [],
    },
  },

  data() {
    return {
      localFeaturedProducts: this.featuredProducts ? [...this.featuredProducts] : [],
    };
  },

  mixins: [SearchMixin],

  computed: {
    ...mapState(useUserStore, ["isAdmin"]),
  },

  methods: {
    removeFeatured(item) {
      this.localFeaturedProducts = this.localFeaturedProducts.filter((p) => p.id !== item.id);
      useAdminProductsStore()
        .toggleFeatured(item.id, false, item.theme)
        .catch((error) => {
          this.localFeaturedProducts = [...this.localFeaturedProducts, item];
          alert("Error updating featured status: " + (error?.response?.data?.message || error.message));
        });
    },

    mapBillboardColor(color) {
      switch (color) {
        case "blue":
          return "blue";
        case "black":
          return "black";
        case "red":
          return "red";
        case "grey":
          return "black";
        case "orange":
          return "gold";
        case "green":
          return "green";
        default:
          return "red";
      }
    },
  },

  mounted() {
    const searchParams = new URLSearchParams(window.location.search);

    if (searchParams.has("scrollToResults")) {
      setTimeout(() => {
        this.$scrollTo("#search-content");
      }, 100);
    }
  },
};
</script>
